import type { components } from "@majitap/sdk";
import { queryOptions } from "@tanstack/react-query";
import { api, ok } from "./api";

type S = components["schemas"];
export type Customer = S["Customer"];
export type Location = S["Location"];
export type Tank = S["Tank"];
/** A place belongs to a customer or to a business. */
export type Owner = { customer_id: string } | { business_id: string };

export const customerQuery = (id: string) =>
  queryOptions({
    queryKey: ["customer", id],
    queryFn: () => ok(api.GET("/v1/admin/customers/{id}", { params: { path: { id } } })),
  });

export const placesQuery = (owner: Owner) =>
  queryOptions({
    queryKey: ["places", owner],
    queryFn: async () =>
      (await ok(api.GET("/v1/admin/locations", { params: { query: { ...owner, limit: 100 } } }))).data,
  });

export const tanksQuery = (locationId: string) =>
  queryOptions({
    queryKey: ["tanks", locationId],
    queryFn: async () =>
      (await ok(api.GET("/v1/admin/locations/{id}/tanks", { params: { path: { id: locationId } } }))).data,
  });

/** A tank's sensor and its latest reading (customers.manage). */
export const sensorQuery = (id: string) =>
  queryOptions({
    queryKey: ["sensor", id],
    queryFn: () => ok(api.GET("/v1/admin/sensors/{id}", { params: { path: { id } } })),
  });

/** A sensor's readings in a period, newest first; at most 1,000 (the latest of a long period). */
export const READINGS_CAP = 1000;
export const readingsQuery = (id: string, from: string, to?: string) =>
  queryOptions({
    queryKey: ["readings", id, from, to],
    queryFn: async () =>
      (
        await ok(
          api.GET("/v1/admin/sensors/{id}/readings", {
            params: { path: { id }, query: { from, to, limit: READINGS_CAP } },
          }),
        )
      ).data,
  });

/** A wallet belongs to a customer, a business or a partner. */
export type WalletOwner = Owner | { partner_id: string };

export const walletOfQuery = (owner: WalletOwner) =>
  queryOptions({
    queryKey: ["wallet-of", owner],
    queryFn: async () =>
      (await ok(api.GET("/v1/admin/wallets", { params: { query: { ...owner, limit: 5 } } }))).data[0] ?? null,
  });

export const paymentTermsQuery = queryOptions({
  queryKey: ["payment-terms"],
  staleTime: 5 * 60_000,
  queryFn: async () => (await ok(api.GET("/v1/admin/billing/payment-terms"))).data,
});

export const customerActions = {
  create: (body: components["schemas"]["CreateCustomerBody"]) => ok(api.POST("/v1/admin/customers", { body })),
  update: (id: string, body: components["schemas"]["UpdateCustomerBody"]) =>
    ok(api.PATCH("/v1/admin/customers/{id}", { params: { path: { id } }, body })),
  setActive: (id: string, active: boolean) =>
    active
      ? ok(api.POST("/v1/admin/customers/{id}/reactivate", { params: { path: { id } } }))
      : ok(api.POST("/v1/admin/customers/{id}/deactivate", { params: { path: { id } } })),
};

export interface PlaceFields {
  label: string;
  address_line?: string;
  landmark?: string;
  floor?: number;
  contact_name?: string;
  contact_phone?: string;
  latitude: number;
  longitude: number;
}

/** On an edit, a field left empty is cleared. */
const cleared = (body: Partial<PlaceFields>) => ({
  ...body,
  address_line: body.address_line ?? null,
  landmark: body.landmark ?? null,
  floor: body.floor ?? null,
  contact_name: body.contact_name ?? null,
  contact_phone: body.contact_phone ?? null,
});

export const placeActions = {
  create: (owner: Owner, body: PlaceFields) => ok(api.POST("/v1/admin/locations", { body: { ...owner, ...body } })),
  update: (id: string, body: Partial<PlaceFields>) =>
    ok(api.PATCH("/v1/admin/locations/{id}", { params: { path: { id } }, body: cleared(body) })),
  remove: (id: string) => ok(api.DELETE("/v1/admin/locations/{id}", { params: { path: { id } } })),
  // only the flag: an edit through `update` would clear the fields it isn't given
  makeDefault: (id: string) =>
    ok(api.PATCH("/v1/admin/locations/{id}", { params: { path: { id } }, body: { is_default: true } })),
  addTank: (locationId: string, body: { label?: string; capacity_litres?: number; low_level_percent?: string }) =>
    ok(api.POST("/v1/admin/locations/{id}/tanks", { params: { path: { id: locationId } }, body })),
  updateTank: (
    locationId: string,
    tankId: string,
    // null removes a label or size; is_active false retires the tank
    body: { label?: string | null; capacity_litres?: number | null; low_level_percent?: string; is_active?: boolean },
  ) =>
    ok(
      api.PATCH("/v1/admin/locations/{id}/tanks/{tank_id}", {
        params: { path: { id: locationId, tank_id: tankId } },
        body,
      }),
    ),
};

export const creditActions = {
  setLimit: (customerId: string, credit_limit: string, reason: string) =>
    ok(
      api.PUT("/v1/admin/billing/customers/{id}/credit-limit", {
        params: { path: { id: customerId } },
        body: { credit_limit, reason },
      }),
    ),
  setTerm: (customerId: string, payment_term_id: string | null) =>
    ok(
      api.PUT("/v1/admin/billing/customers/{id}/payment-term", {
        params: { path: { id: customerId } },
        body: { payment_term_id },
      }),
    ),
};

/** A decimal amount as the API takes it: digits, at most two decimals. */
export const isAmount = (value: string) => /^\d{1,12}(\.\d{1,2})?$/.test(value.trim());
